import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { RouteFallback } from '@/routes/lazy';
import {
  Activity,
  BarChart3,
  Building2,
  ClipboardList,
  CreditCard,
  Flag,
  LayoutDashboard,
  LogOut,
  MapPin,
  Moon,
  Settings,
  Sparkles,
  Sun,
  Upload,
  ShieldCheck,
  Users,
  Briefcase,
  BookOpen,
} from 'lucide-react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useAdminTheme } from '../hooks/useAdminTheme';
import { useLogout } from '@/features/auth/api';
import { MASTER_DATA_REGISTRY } from '../master-data/registry';
import '@/styles/admin.css';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

const I = { size: 16 };

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/admin', icon: <LayoutDashboard {...I} /> },
  { label: 'Users', to: '/admin/users', icon: <Users {...I} /> },
  { label: 'Organizations', to: '/admin/organizations', icon: <Building2 {...I} /> },
  { label: 'Pending Verif.', to: '/admin/organizations/pending', icon: <ShieldCheck {...I} /> },
  { label: 'Jobs', to: '/admin/jobs', icon: <Briefcase {...I} /> },
  { label: 'Assessments', to: '/admin/assessments', icon: <ClipboardList {...I} /> },
  { label: 'Credits', to: '/admin/credits', icon: <CreditCard {...I} /> },
  { label: 'Feature Flags', to: '/admin/flags', icon: <Flag {...I} /> },
  { label: 'Admin Team', to: '/admin/team', icon: <Settings {...I} /> },
  { label: 'Activity Log', to: '/admin/activity', icon: <Activity {...I} /> },
  // Opens the handbook's own full-screen layout (features/handbook).
  { label: 'Platform Handbook', to: '/admin/handbook', icon: <BookOpen {...I} /> },
];

const MASTER_DATA_NAV_ITEMS: NavItem[] = [
  { label: 'Location Suggestions', to: '/admin/locations/suggestions', icon: <MapPin {...I} /> },
  { label: 'Bulk Upload', to: '/admin/locations/bulk', icon: <Upload {...I} /> },
  { label: 'Skill Suggestions', to: '/admin/skills/suggestions', icon: <Sparkles {...I} /> },
  // Registered catalog entities (Skills, ...) — adding one to MASTER_DATA_REGISTRY
  // is enough to get a nav link + list/edit/delete page, no route file needed.
  ...MASTER_DATA_REGISTRY.map((entity) => ({
    label: entity.navLabel,
    to: `/admin/master-data/${entity.slug}`,
    icon: entity.icon,
  })),
];

// Visible to SUPER_ADMIN and FINANCE_ADMIN; the API rejects other roles.
const BILLING_NAV_ITEMS: NavItem[] = [
  { label: 'Subscriptions', to: '/admin/billing/subscriptions', icon: <CreditCard {...I} /> },
  { label: 'Invoices', to: '/admin/billing/invoices', icon: <ClipboardList {...I} /> },
  { label: 'Dunning', to: '/admin/billing/dunning', icon: <Activity {...I} /> },
  { label: 'Entitlements', to: '/admin/billing/entitlements', icon: <ShieldCheck {...I} /> },
  { label: 'Reports', to: '/admin/billing/reports', icon: <BarChart3 {...I} /> },
];

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAdminAuth();
  const logout = useLogout();
  const { isDark, toggleTheme } = useAdminTheme();

  const isActive = (to: string) => {
    if (to === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(to);
  };

  const handleLogout = () => {
    logout.mutate(undefined, { onSettled: () => navigate('/auth/login', { replace: true }) });
  };

  const displayName = user?.email ?? '';

  const renderNavItem = (item: NavItem) => {
    const active = isActive(item.to);
    return (
      <Link
        key={item.to}
        to={item.to}
        className={`adm-nav-item${active ? ' adm-nav-item--active' : ''}`}
        aria-current={active ? 'page' : undefined}
      >
        <span className="adm-nav-icon" aria-hidden="true">
          {item.icon}
        </span>
        {item.label}
      </Link>
    );
  };

  return (
    <div className="adm-shell" data-theme={isDark ? 'dark' : 'light'}>
      <a href="#adm-main-content" className="adm-skip-link">
        Skip to content
      </a>

      <aside className="adm-sidebar">
        <div className="adm-brand">
          <Link to="/admin" className="adm-logo">
            Welynk
          </Link>
          <span className="adm-badge">Admin</span>
        </div>

        <nav className="adm-nav" aria-label="Admin navigation">
          {NAV_ITEMS.map(renderNavItem)}

          <div className="adm-nav-section" id="adm-nav-group-billing">
            Billing
          </div>
          <div role="group" aria-labelledby="adm-nav-group-billing">
            {BILLING_NAV_ITEMS.map(renderNavItem)}
          </div>

          <div className="adm-nav-section" id="adm-nav-group-master-data">
            Master data
          </div>
          <div role="group" aria-labelledby="adm-nav-group-master-data">
            {MASTER_DATA_NAV_ITEMS.map(renderNavItem)}
          </div>
        </nav>

        <div className="adm-sidebar-foot">
          <button
            type="button"
            className="adm-theme-toggle"
            onClick={toggleTheme}
            aria-pressed={isDark}
          >
            {isDark ? <Sun size={14} aria-hidden="true" /> : <Moon size={14} aria-hidden="true" />}
            {isDark ? 'Light mode' : 'Dark mode'}
          </button>
          <Link
            to="/personal/dashboard"
            className="adm-nav-item"
            style={{ fontSize: 12, opacity: 0.7 }}
          >
            ← Back to App
          </Link>
          <div
            className="adm-nav-item"
            style={{ fontSize: 12, color: 'var(--adm-sidebar-text-dim)', gap: 6 }}
          >
            <BarChart3 size={14} aria-hidden="true" />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </span>
          </div>
          <button className="adm-nav-item adm-nav-item--danger" onClick={handleLogout}>
            <LogOut size={14} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </aside>

      <main className="adm-main" id="adm-main-content" tabIndex={-1}>
        {/* Admin pages are lazy chunks — keep the admin chrome mounted while loading. */}
        <React.Suspense fallback={<RouteFallback />}>
          <Outlet />
        </React.Suspense>
      </main>
    </div>
  );
};
